import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, addDays, isBefore, isSameDay, addWeeks, startOfDay } from 'date-fns';
import { getDateLocale } from '@/utils/dateLocale';
import { ArrowRight, CalendarX2, ChevronLeft, ChevronRight, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TimeSlotSkeleton } from '@/components/booking/SkeletonLoader';
import { TimeSlotGrid, type TimeSlot as GridTimeSlot } from '@/components/booking/TimeSlotGrid';
import { TimeOfDayTabs, timeOfDay, type TimeOfDay } from '@/components/booking/TimeOfDayTabs';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { PersonPicker } from '../frame/PersonPicker';
import type { DayKey, PublicOrganization } from '../../booking-page/types';
import type { Service, Provider } from '@/types/booking';
import type { NormalizedSlot } from '@/utils/normalizeSlot';

// date-fns already cases each language correctly -- Portuguese keeps weekdays
// and months lower case, German capitalises them -- so only the first letter of
// the finished string is raised. The CSS `capitalize` used before raised every
// word, which turned "quinta-feira 24 setembro" into "Quinta-Feira 24 Setembro".
const sentenceCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

interface DateTimeSelectionProps {
  // Booking page data (opening hours for "Closed" days, contact for the
  // no-times card). Optional so the step also renders on its own.
  organization?: PublicOrganization;
  selectedService: Service | null;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  currentWeekStart: Date;
  setCurrentWeekStart: (date: Date) => void;
  datesWithSlots: Set<string>;
  hasFutureSlots: boolean | null;
  availableSlots: NormalizedSlot[];
  selectedSlot: NormalizedSlot | null;
  onSelectSlot: (slot: NormalizedSlot) => void;
  isLoadingSlots: boolean;
  providers: Provider[];
  selectedProvider: Provider | null;
  onSelectProvider: (provider: Provider | null) => void;
}

const DAY_KEYS: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function DateTimeSelection({
  organization,
  selectedService,
  selectedDate,
  setSelectedDate,
  currentWeekStart,
  setCurrentWeekStart,
  datesWithSlots,
  hasFutureSlots,
  availableSlots,
  selectedSlot,
  onSelectSlot,
  isLoadingSlots,
  providers,
  selectedProvider,
  onSelectProvider,
}: DateTimeSelectionProps) {
  const { t } = useTranslation(['bookingPage', 'publicBooking']);
  const locale = getDateLocale();
  const today = startOfDay(new Date());
  const hours = organization?.booking_page?.hours;
  const weekEnd = addDays(currentWeekStart, 6);

  // A day is "Closed" when the published opening hours have nothing for that
  // weekday and there are no free times anyway.
  const closedOn = (date: Date, hasSlots: boolean) => {
    if (hasSlots || !hours) return false;
    const day = hours.days.find((d) => d.day === DAY_KEYS[date.getDay()]);
    return !!day && day.ranges.length === 0;
  };

  // Next day in view with free times, for the empty-day card.
  const nextFreeDay = useMemo(() => {
    const later = [...datesWithSlots].sort().find((d) => d > format(selectedDate, 'yyyy-MM-dd'));
    return later ? parseISO(later) : null;
  }, [datesWithSlots, selectedDate]);

  if (hasFutureSlots === false) {
    return <NoTimesCard organization={organization} />;
  }

  const monthLabel = format(currentWeekStart, 'LLLL yyyy', { locale });
  const rangeLabel = `${format(currentWeekStart, 'd MMM', { locale })} – ${format(weekEnd, 'd MMM', { locale })}`;
  const canGoBack = !isBefore(addDays(currentWeekStart, -1), today);

  return (
    <>
      {providers.length > 1 && (
        <>
          <PersonPicker providers={providers} selected={selectedProvider} onSelect={onSelectProvider} />
          <div className="hidden h-px bg-line sm:block" />
        </>
      )}

      {/* Week strip */}
      <section className="flex flex-col gap-3" data-testid="week-strip">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-[16px] font-semibold text-ink">{sentenceCase(monthLabel)}</span>
            <span className="text-[13px] text-soft">{rangeLabel}</span>
          </div>
          <span className="flex gap-1.5">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-surface text-ink hover:border-brand-300 disabled:opacity-40"
              onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, -1))}
              disabled={!canGoBack}
              aria-label={t('publicBooking:dateTime.aria.previousWeek')}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-surface text-ink hover:border-brand-300"
              onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}
              aria-label={t('publicBooking:dateTime.aria.nextWeek')}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </span>
        </div>

        <div className="-mr-4 flex snap-x gap-1.5 overflow-x-auto pb-1 pr-4 scrollbar-none sm:mr-0 sm:gap-2 sm:overflow-visible sm:pr-0">
          {Array.from({ length: 7 }, (_, i) => {
            const date = addDays(currentWeekStart, i);
            const isSelected = isSameDay(date, selectedDate);
            const isPast = isBefore(date, today);
            const hasSlots = datesWithSlots.has(format(date, 'yyyy-MM-dd'));
            const isClosed = !isPast && closedOn(date, hasSlots);
            const disabled = isPast || isClosed;
            return (
              <button
                key={i}
                type="button"
                disabled={disabled}
                aria-pressed={isSelected}
                aria-label={format(date, 'EEEE, d MMMM yyyy', { locale })}
                data-testid="day-tile"
                data-has-slots={hasSlots || undefined}
                onClick={() => setSelectedDate(date)}
                className={cn(
                  'flex h-[76px] w-[62px] shrink-0 snap-start flex-col items-center justify-center gap-[3px] rounded-xl text-center transition-colors sm:w-auto sm:flex-1',
                  isSelected
                    ? 'border border-brand-500 bg-brand-500 text-white'
                    : isPast
                      ? 'border border-surface-2 bg-surface-2 text-text-muted'
                      : isClosed
                        ? 'border border-dashed border-line bg-surface text-text-muted'
                        : 'border border-line bg-surface text-ink hover:border-brand-300',
                )}
              >
                <span className={cn('text-[12px] font-medium', isSelected ? 'text-white/85' : disabled ? 'text-text-muted' : 'text-soft')}>
                  {format(date, 'EEE', { locale })}
                </span>
                <span className="text-[19px] font-semibold tabular-nums">{format(date, 'd')}</span>
                {isClosed ? (
                  <span className="text-[10px]">{t('steps.closed')}</span>
                ) : (
                  <span className={cn('h-2 w-2 rounded-pill', hasSlots && !isPast ? (isSelected ? 'bg-white' : 'bg-success') : 'bg-transparent')} />
                )}
              </button>
            );
          })}
        </div>
      </section>

      <TimeSlotsSection
        availableSlots={availableSlots}
        selectedSlot={selectedSlot}
        onSelectSlot={onSelectSlot}
        isLoadingSlots={isLoadingSlots}
        selectedDate={selectedDate}
        isGroupService={(selectedService?.max_capacity ?? 1) > 1}
        nextFreeDay={nextFreeDay}
        onJumpTo={setSelectedDate}
        onNextWeek={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}
        manyProviders={providers.length > 1}
      />
    </>
  );
}

// Nothing bookable in the next 30 days: say so and offer the contact options.
function NoTimesCard({ organization }: { organization?: PublicOrganization }) {
  const { t } = useTranslation(['bookingPage', 'publicBooking']);
  const page = organization?.booking_page;
  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-line bg-surface p-5" data-testid="no-times">
      <span className="text-[16px] font-semibold text-ink">{t('steps.noTimes.title')}</span>
      <span className="text-[14px] leading-normal text-soft">
        {organization ? t('steps.noTimes.body', { name: organization.name }) : t('publicBooking:dateTime.noFutureSlots')}
      </span>
      {(page?.phone || page?.email) && (
        <div className="flex gap-2">
          {page.phone && (
            <Button asChild variant="outline" className="h-11">
              <a href={`tel:${page.phone.replace(/\s+/g, '')}`}><Phone className="h-4 w-4" />{t('contact.call')}</a>
            </Button>
          )}
          {page.email && (
            <Button asChild variant="outline" className="h-11">
              <a href={`mailto:${page.email}`}><Mail className="h-4 w-4" />{t('contact.email')}</a>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Time-slots subsection ────────────────────────────────────────────────

interface TimeSlotsSectionProps {
  availableSlots: NormalizedSlot[];
  selectedSlot: NormalizedSlot | null;
  onSelectSlot: (slot: NormalizedSlot) => void;
  isLoadingSlots: boolean;
  selectedDate: Date;
  isGroupService?: boolean;
  nextFreeDay?: Date | null;
  onJumpTo?: (date: Date) => void;
  onNextWeek?: () => void;
  // The organization has more than one bookable person, so each time needs to
  // say who it is with.
  manyProviders: boolean;
}

function TimeSlotsSection({
  availableSlots,
  selectedSlot,
  onSelectSlot,
  isLoadingSlots,
  selectedDate,
  isGroupService = false,
  nextFreeDay,
  onJumpTo,
  onNextWeek,
  manyProviders,
}: TimeSlotsSectionProps) {
  const { t } = useTranslation(['bookingPage', 'publicBooking']);
  const locale = getDateLocale();

  // Bucket slots by Morning / Afternoon / Evening.
  // Sort defensively before bucketing — upstream useBookingFlow already sorts,
  // but this component shouldn't trust array order at all input call sites.
  const sortedSlots = useMemo(
    () =>
      [...availableSlots]
        .filter((slot) => !!slot.startTime)
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
    [availableSlots],
  );
  const buckets = useMemo(() => {
    const out: Record<TimeOfDay, NormalizedSlot[]> = { morning: [], afternoon: [], evening: [] };
    for (const slot of sortedSlots) out[timeOfDay(parseISO(slot.startTime))].push(slot);
    return out;
  }, [sortedSlots]);

  const counts: Record<TimeOfDay, number> = {
    morning: buckets.morning.length,
    afternoon: buckets.afternoon.length,
    evening: buckets.evening.length,
  };

  // Default to the first non-empty bucket; recompute when slots change.
  const [bucket, setBucket] = useState<TimeOfDay>('morning');
  useEffect(() => {
    if (availableSlots.length === 0) return;
    if (counts[bucket] === 0) {
      const first = (['morning', 'afternoon', 'evening'] as TimeOfDay[]).find(
        (k) => counts[k] > 0,
      );
      if (first) setBucket(first);
    }
  }, [availableSlots.length, counts.morning, counts.afternoon, counts.evening]);

  const labels: Record<TimeOfDay, string> = {
    morning: t('publicBooking:dateTime.morning', 'Morning'),
    afternoon: t('publicBooking:dateTime.afternoon', 'Afternoon'),
    evening: t('publicBooking:dateTime.evening', 'Evening'),
  };

  // Group sessions carry capacity info and are few per day: one list, no
  // time-of-day tabs.
  const isGroupList = isGroupService || sortedSlots.some((slot) => slot.maxCapacity > 1);
  // Group times are full-width rows on phones; from sm up they sit in a grid
  // so a lone time doesn't stretch across the whole column.
  const wideGroupGrid = useMediaQuery('(min-width: 640px)');
  const shownSlots = isGroupList ? sortedSlots : buckets[bucket];

  const gridSlots: GridTimeSlot[] = useMemo(
    () =>
      shownSlots.map((slot) => {
        const waitlistable = slot.isFull && slot.waitlistEnabled;
        return {
          id: String(slot.id),
          start: parseISO(slot.startTime),
          sublabel: manyProviders ? slot.providerName || undefined : undefined,
          taken: slot.isFull && !waitlistable,
          disabled: slot.isFull && !waitlistable,
          footer: slot.maxCapacity > 1 ? <CapacityBar slot={slot} inline={isGroupList} /> : undefined,
        };
      }),
    [shownSlots, isGroupList, manyProviders],
  );

  const dayTitle = format(selectedDate, 'EEEE d MMMM', { locale });
  const header = (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[16px] font-semibold text-ink">{sentenceCase(dayTitle)}</span>
      {!isLoadingSlots && availableSlots.length > 0 && (
        <span className="text-[13px] text-soft">
          {isGroupList ? t('steps.sessionCount', { count: sortedSlots.length }) : t('steps.freeTimes', { count: sortedSlots.length })}
        </span>
      )}
    </div>
  );

  if (isLoadingSlots) {
    return (
      <section className="flex flex-col gap-3">
        {header}
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => <TimeSlotSkeleton key={i} />)}
        </div>
      </section>
    );
  }

  if (availableSlots.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2.5 rounded-2xl border border-line bg-surface px-5 py-6 text-center" data-testid="empty-day">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-[14px] bg-surface-2">
          <CalendarX2 className="h-[22px] w-[22px] text-soft" />
        </span>
        <span className="text-[16px] font-semibold text-ink">
          {t('steps.emptyDay.title', { date: format(selectedDate, 'EEE d MMM', { locale }) })}
        </span>
        <span className="text-[14px] text-soft">{t('steps.emptyDay.body')}</span>
        {nextFreeDay && onJumpTo ? (
          <Button variant="outline" className="mt-1 h-12 w-full sm:w-auto" onClick={() => onJumpTo(nextFreeDay)} data-testid="next-free-day">
            {t('steps.emptyDay.nextFree', { date: format(nextFreeDay, 'EEE d MMM', { locale }) })}
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : onNextWeek ? (
          <Button variant="outline" className="mt-1 h-12 w-full sm:w-auto" onClick={onNextWeek} data-testid="next-week">
            {t('steps.emptyDay.nextWeek')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3" data-testid="time-section">
      {header}
      {!isGroupList && (
        <TimeOfDayTabs value={bucket} onValueChange={setBucket} counts={counts} labels={labels} variant="segmented" />
      )}
      <TimeSlotGrid
        layout={isGroupList ? (wideGroupGrid ? 'cards' : 'list') : 'grid'}
        columnsClassName={isGroupList ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-3 sm:grid-cols-4'}
        size="lg"
        slots={gridSlots}
        selectedId={selectedSlot ? String(selectedSlot.id) : undefined}
        onSelect={(gs) => {
          const original = shownSlots.find((s) => String(s.id) === gs.id);
          if (original) onSelectSlot(original);
        }}
        emptyTitle={t('publicBooking:dateTime.noSlots')}
      />
      <span className="text-[13px] text-soft">
        {t('steps.timezone', { zone: Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ') })}
      </span>
    </section>
  );
}

interface CapacityBarProps {
  slot: NormalizedSlot;
  /** Render as the right-hand side of a list row (group classes). */
  inline?: boolean;
}

function CapacityBar({ slot, inline = false }: CapacityBarProps) {
  const { t } = useTranslation(['bookingPage', 'publicBooking']);
  const { maxCapacity, spotsRemaining, isFull, waitlistEnabled } = slot;
  const pct = isFull ? 1 : 1 - spotsRemaining / maxCapacity;
  const waitlistable = isFull && waitlistEnabled;

  const badge = waitlistable ? (
    <span className="inline-flex h-[26px] items-center whitespace-nowrap rounded-pill bg-brand-100 px-2.5 text-[12px] font-semibold text-brand-700" data-testid="badge-waitlist">
      {t('publicBooking:capacity.joinWaitlist', 'Join waitlist')}
    </span>
  ) : isFull ? (
    <span className="inline-flex h-[26px] items-center whitespace-nowrap rounded-pill bg-surface-2 px-2.5 text-[12px] font-semibold text-soft" data-testid="badge-full">
      {t('steps.full')}
    </span>
  ) : null;

  const bar = (
    <span className={cn('flex flex-col gap-1.5', inline ? 'min-w-0 flex-1' : 'w-full')}>
      <span className="h-1.5 overflow-hidden rounded-pill bg-surface-2">
        <span
          className={cn('block h-full rounded-pill', isFull ? 'bg-line-strong' : spotsRemaining / maxCapacity > 0.3 ? 'bg-success' : 'bg-warn')}
          style={{ width: `${Math.max(pct, 0.04) * 100}%` }}
        />
      </span>
      <span className="truncate text-[12px] text-soft">
        {isFull
          ? /* The badge beside it already says "waitlist", so the label only
               has to carry the state. */
            t('steps.full')
          : t('steps.placesLeft', { count: spotsRemaining, total: maxCapacity })}
      </span>
    </span>
  );

  if (inline) {
    return (
      <span className="flex items-center gap-3">
        {bar}
        <span className="shrink-0">{badge}</span>
      </span>
    );
  }
  return (
    <span className="flex w-full flex-col items-center gap-1.5">
      {bar}
      {badge}
    </span>
  );
}
